"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useId, useState } from "react";

export default function LoginForm({ oidcResume = false }: { oidcResume?: boolean }) {
  const router = useRouter();
  const fieldId = useId();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setError("");
    setLoading(true);

    try {
      const response = await fetch("/api/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          identifier,
          password,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        setError(data?.message || "로그인에 실패했습니다.");
        return;
      }

      if (oidcResume) {
        window.location.assign("/oidc/resume");
        return;
      }
      router.push("/my");
      router.refresh();
    } catch {
      setError("로그인 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full max-w-md rounded-lg border border-[#e5e1e9] bg-[#f8f7fb] p-6"
    >
      <div>
        <label htmlFor={`${fieldId}-identifier`} className="text-sm font-bold text-slate-300">아이디 또는 이메일</label>
        <input
          id={`${fieldId}-identifier`}
          type="text"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          placeholder="아이디 또는 example@email.com"
          value={identifier}
          onChange={(event) => setIdentifier(event.target.value)}
          className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-900 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-violet-400"
          required
        />
      </div>

      <div className="mt-5">
        <label htmlFor={`${fieldId}-password`} className="text-sm font-bold text-slate-300">비밀번호</label>
        <input
          id={`${fieldId}-password`}
          type="password"
          autoComplete="current-password"
          placeholder="비밀번호 입력"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-900 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-violet-400"
          required
        />
      </div>

      {error && (
        <p role="alert" className="mt-4 rounded-md border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="mt-6 w-full rounded-2xl bg-violet-500 px-5 py-3 font-bold text-white transition hover:bg-violet-400 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? "로그인 중..." : "로그인"}
      </button>

      <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
        <Link
          href="/account/recovery"
          className="text-violet-300 hover:underline"
        >
          아이디·비밀번호 찾기
        </Link>
        <Link href="/register" className="text-slate-400 hover:text-violet-300">
          회원가입
        </Link>
      </div>
    </form>
  );
}
